import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WalletProvider } from './contexts/WalletContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Public Pages
import PaymentPage from './pages/PaymentPage';
import WebhookTestPage from './pages/WebhookTestPage';
import PublicApiDocsPage from './pages/PublicApiDocsPage';

// Merchant Pages
import LoginPage from './pages/LoginPage';
import ForceChangePasswordPage from './pages/ForceChangePasswordPage';
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import CreateOrderPage from './pages/CreateOrderPage';
import WalletPage from './pages/WalletPage';
import SettlementPage from './pages/SettlementPage';
import PayoutsPage from './pages/PayoutsPage'; // ⭐ NEW
import DepositPage from './pages/DepositPage'; // ⭐ P2P Deposit
import P2pPayoutsPage from './pages/P2pPayoutsPage'; // ⭐ P2P liquidity (ถอน)
import P2pOverviewPage from './pages/P2pOverviewPage'; // ⭐ P2P Overview (merchant)
import CreatePayoutPage from './pages/CreatePayoutPage';
import ApiKeysPage from './pages/ApiKeysPage';
import ApiDocsPage from './pages/ApiDocsPage';
import WebhookSettingsPage from './pages/WebhookSettingsPage';
import TelegramSettingsPage from './pages/TelegramSettingsPage';
import TwoFAPage from './pages/TwoFAPage';
import IPWhitelistPage from './pages/IPWhitelistPage';
import EmployeesPage from './pages/EmployeesPage';
import ProfilePage from './pages/ProfilePage';
import AnalyticsPage from './pages/AnalyticsPage';
import MerchantMonthlyReportsPage from './pages/MerchantMonthlyReportsPage';
import SafeWalletPage from './pages/SafeWalletPage';
import CompanyTransferPage from './pages/CompanyTransferPage';
import MerchantWalletTransactionsPage from './pages/WalletTransactionsPage';
import TicketsPage from './pages/TicketsPage';
import TicketDetailPage from './pages/TicketDetailPage';
import Layout from './components/Layout';
import RequirePermission from './components/RequirePermission';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import MerchantsManagementPage from './pages/admin/MerchantsManagementPage';
import SystemReportsPage from './pages/admin/SystemReportsPage';
import MonthlyReportsPage from './pages/admin/MonthlyReportsPage';
import AllOrdersPage from './pages/admin/AllOrdersPage';
import PayoutTopupsPage from './pages/admin/PayoutTopupsPage';
import AllTransactionsPage from './pages/admin/AllTransactionsPage';
import WebhookLogsPage from './pages/admin/WebhookLogsPage';
import BanksManagementPage from './pages/admin/BanksManagementPage';
import SettlementManagementPage from './pages/admin/SettlementManagementPage';
import SettlementCalcPage from './pages/admin/SettlementCalcPage';
import BankCapacityPage from './pages/admin/bank-capacity/BankCapacityPage'; // 📊 ความจุบัญชี (เพดาน vs ของจริง)
import AdminPayoutsPage from './pages/admin/PayoutsPage'; // ⭐ NEW
import AdminDepositOrdersPage from './pages/admin/DepositOrdersPage'; // ⭐ P2P Deposit
import AdminP2pPayoutsPage from './pages/admin/P2pPayoutsPage'; // ⭐ P2P Payouts (สภาพคล่อง)
import AdminP2pOverviewPage from './pages/admin/P2pOverviewPage'; // ⭐ P2P Overview (fee wallet)
import SystemSettingsPage from './pages/admin/SystemSettingsPage';
import EmployeesReportPage from './pages/admin/EmployeesReportPage';
import AdminEmployeesPage from './pages/admin/AdminEmployeesPage'; // ⭐ NEW
import Admin2FAPage from './pages/admin/Admin2FAPage'; // ⭐ NEW
import AdminIPWhitelistPage from './pages/admin/AdminIPWhitelistPage';
import OnlineUsersPage from './pages/admin/OnlineUsersPage'; // 🟢 NEW - Online Users Monitor
import ResellersManagementPage from './pages/admin/ResellersManagementPage';
import MerchantRequestsPage from './pages/admin/MerchantRequestsPage';
import ResellerReportsPage from './pages/admin/ResellerReportsPage';
import ResellerWithdrawalsPage from './pages/admin/ResellerWithdrawalsPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';
import BulkTransferPage from './pages/admin/BulkTransferPage';
import MismatchedAccountsPage from './pages/admin/MismatchedAccountsPage';
import FreezeManagementPage from './pages/admin/FreezeManagementPage';
import SafeWalletManagementPage from './pages/admin/SafeWalletManagementPage';
import UsdtWalletPage from './pages/admin/UsdtWalletPage';
import AdminCompanyTransferPage from './pages/admin/AdminCompanyTransferPage';
import ReduceCreditPage from './pages/admin/ReduceCreditPage'; // 🔻 ลดเครดิต Merchant (superadmin)
import BankSuspensionPage from './pages/admin/BankSuspensionPage';
import ReconciliationPage from './pages/admin/ReconciliationPage';
import FeeReportPage from './pages/admin/FeeReportPage';
import WalletTransactionsPage from './pages/admin/WalletTransactionsPage';
import AdminTicketsPage from './pages/admin/AdminTicketsPage';
import AdminTicketDetailPage from './pages/admin/AdminTicketDetailPage';
import AgentBanksPage from './pages/admin/AgentBanksPage'; // ⭐ Agent Bank Management
import AgentCommissionsPage from './pages/admin/AgentCommissionsPage'; // ⭐ Agent Commission Overview
import AgentCommissionDetailPage from './pages/admin/AgentCommissionDetailPage';
import TiersPage from './pages/admin/TiersPage'; // ⭐ ระดับร้านค้า (merchant tier)
import AdminLayout from './components/AdminLayout';

// Agent Portal
import AgentLoginPage from './pages/agent/AgentLoginPage';
import AgentDashboardPage from './pages/agent/AgentDashboardPage';
import AgentLayout from './components/AgentLayout';
import AgentTransactionsPage from './pages/agent/AgentTransactionsPage';

// Reseller Pages
import ResellerLoginPage from './pages/reseller/ResellerLoginPage';
import ResellerDashboardPage from './pages/reseller/ResellerDashboardPage';
import ResellerMerchantsPage from './pages/reseller/ResellerMerchantsPage';
import MerchantRequestPage from './pages/reseller/MerchantRequestPage';
import ResellerWalletPage from './pages/reseller/ResellerWalletPage';
import ResellerOwnReportsPage from './pages/reseller/ResellerReportsPage';
import ResellerMonthlyReportsPage from './pages/reseller/ResellerMonthlyReportsPage';
import ResellerSubResellersPage from './pages/reseller/ResellerSubResellersPage';
import ResellerLayout from './components/ResellerLayout';

// Protected Route Component (Merchant)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, mustChangePassword, loading } = useAuth();

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div className="spinner"></div>
    </div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // 🔒 บังคับเปลี่ยนรหัสผ่านครั้งแรก — block ทุกหน้าจนกว่าจะเปลี่ยนเสร็จ
  if (mustChangePassword) {
    return <ForceChangePasswordPage />;
  }

  return <>{children}</>;
};

// Protected Route Component (Admin) - ใช้ state + API check แทน localStorage
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    checkAdminAuth();
  }, []);

  const checkAdminAuth = async () => {
    try {
      // ✅ เช็ค auth ด้วย API (cookie จะถูกส่งไปอัตโนมัติ)
      const { merchantApi } = await import('./api/config');
      await merchantApi.get('/auth/admin/me');
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" />;
  }

  return <>{children}</>;
};

// Protected Route Component (Agent Bank)
const AgentProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    checkAgentAuth();
  }, []);

  const checkAgentAuth = async () => {
    try {
      const { merchantApi } = await import('./api/config');
      const response = await merchantApi.get('/agent/auth/me');
      if (response.data.data) {
        localStorage.setItem('agent_user', JSON.stringify(response.data.data));
      }
      setIsAuthenticated(true);
    } catch {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/agent/login" />;
  }

  return <>{children}</>;
};

// Protected Route Component (Reseller)
const ResellerProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    checkResellerAuth();
  }, []);

  const checkResellerAuth = async () => {
    try {
      const { merchantApi } = await import('./api/config');
      const response = await merchantApi.get('/auth/reseller/me');
      // อัพเดท localStorage ด้วย
      if (response.data.data?.user) {
        localStorage.setItem('reseller_user', JSON.stringify(response.data.data.user));
      }
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/reseller/login" />;
  }

  return <>{children}</>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Payment Page */}
      <Route path="/pay/:order_number" element={<PaymentPage />} />
      
      {/* Webhook Test Page (Public) */}
      <Route path="/webhook-test" element={<WebhookTestPage />} />

      {/* Public API Docs (no login required) */}
      <Route path="/docs" element={<PublicApiDocsPage />} />

      {/* Merchant Routes */}
      <Route path="/login" element={<LoginPage />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          <WalletProvider>
            <Layout />
          </WalletProvider>
        </ProtectedRoute>
      }>
        <Route index element={<RequirePermission permission="can_view_dashboard"><DashboardPage /></RequirePermission>} />
        <Route path="analytics" element={<RequirePermission permission="can_view_analytics"><AnalyticsPage /></RequirePermission>} />
        <Route path="orders" element={<RequirePermission permission="can_view_orders"><OrdersPage /></RequirePermission>} />
        <Route path="create-order" element={<RequirePermission permission="can_create_order"><CreateOrderPage /></RequirePermission>} />
        <Route path="wallet" element={<RequirePermission permission="can_manage_settlement"><WalletPage /></RequirePermission>} />
        <Route path="safe-wallet" element={<RequirePermission permission="can_manage_settlement"><SafeWalletPage /></RequirePermission>} />
        <Route path="company-transfer" element={<RequirePermission ownerOnly><CompanyTransferPage /></RequirePermission>} />
        <Route path="settlement" element={<RequirePermission permission={['can_create_settlement', 'can_view_settlement']}><SettlementPage /></RequirePermission>} />
        <Route path="settlement-list" element={<RequirePermission permission={['can_create_settlement', 'can_view_settlement']}><SettlementPage /></RequirePermission>} />
        <Route path="payouts" element={<RequirePermission permission="can_view_payouts"><PayoutsPage /></RequirePermission>} />
        <Route path="create-payout" element={<RequirePermission permission="can_create_payout"><CreatePayoutPage /></RequirePermission>} />
        <Route path="deposit" element={<DepositPage />} /> {/* ⭐ P2P Deposit */}
        <Route path="p2p-payouts" element={<P2pPayoutsPage />} /> {/* ⭐ P2P liquidity (ถอน) */}
        <Route path="p2p-overview" element={<P2pOverviewPage />} /> {/* ⭐ P2P Overview */}
        <Route path="api-keys" element={<RequirePermission permission="can_manage_settings"><ApiKeysPage /></RequirePermission>} />
        <Route path="2fa" element={<TwoFAPage />} />
        <Route path="ip-whitelist" element={<RequirePermission permission="can_manage_settings"><IPWhitelistPage /></RequirePermission>} />
        <Route path="employees" element={<RequirePermission permission="can_manage_employees"><EmployeesPage /></RequirePermission>} />
        <Route path="webhook" element={<RequirePermission permission="can_manage_settings"><WebhookSettingsPage /></RequirePermission>} />
        <Route path="telegram" element={<RequirePermission permission="can_manage_settings"><TelegramSettingsPage /></RequirePermission>} />
        <Route path="api-docs" element={<RequirePermission permission="can_manage_settings"><ApiDocsPage /></RequirePermission>} />
        <Route path="monthly-reports" element={<RequirePermission permission="can_view_analytics"><MerchantMonthlyReportsPage /></RequirePermission>} />
        <Route path="wallet-transactions" element={<RequirePermission permission={['can_view_transactions', 'can_manage_settlement']}><MerchantWalletTransactionsPage /></RequirePermission>} />
        <Route path="tickets" element={<RequirePermission permission="can_view_tickets"><TicketsPage /></RequirePermission>} />
        <Route path="tickets/:id" element={<RequirePermission permission="can_view_tickets"><TicketDetailPage /></RequirePermission>} />
        <Route path="profile" element={<ProfilePage />} />
      </Route>

      {/* Reseller Routes */}
      <Route path="/reseller/login" element={<ResellerLoginPage />} />

      <Route path="/reseller" element={
        <ResellerProtectedRoute>
          <ResellerLayout />
        </ResellerProtectedRoute>
      }>
        <Route index element={<ResellerDashboardPage />} />
        <Route path="merchants" element={<ResellerMerchantsPage />} />
        <Route path="merchant-requests" element={<MerchantRequestPage />} />
        <Route path="wallet" element={<ResellerWalletPage />} />
        <Route path="reports" element={<ResellerOwnReportsPage />} />
        <Route path="monthly-reports" element={<ResellerMonthlyReportsPage />} />
        <Route path="sub-reseller" element={<ResellerSubResellersPage />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      
      <Route path="/admin" element={
        <AdminProtectedRoute>
          <AdminLayout />
        </AdminProtectedRoute>
      }>
        <Route index element={<AdminDashboardPage />} />
        <Route path="analytics" element={<AdminAnalyticsPage />} />
        <Route path="merchants" element={<MerchantsManagementPage />} />
        <Route path="merchant-tiers" element={<TiersPage />} /> {/* ⭐ ระดับร้านค้า */}
        <Route path="resellers" element={<ResellersManagementPage />} />
        <Route path="merchant-requests" element={<MerchantRequestsPage />} />
        <Route path="reseller-reports" element={<ResellerReportsPage />} />
        <Route path="reseller-withdrawals" element={<ResellerWithdrawalsPage />} />
        <Route path="banks" element={<BanksManagementPage />} />
        <Route path="bank-suspensions" element={<BankSuspensionPage />} />
        <Route path="orders" element={<AllOrdersPage />} />
        <Route path="transactions" element={<AllTransactionsPage />} />
        <Route path="wallet-transactions" element={<WalletTransactionsPage />} />
        <Route path="settlements" element={<SettlementManagementPage />} />
        <Route path="settlement-calc" element={<SettlementCalcPage />} /> {/* ⚖️ ตัดยอดแบบคำนวณสด ไม่บันทึก */}
        <Route path="bank-capacity" element={<BankCapacityPage />} /> {/* 📊 ความจุบัญชี: เพดาน N/S vs ออเดอร์จริงรายวัน (what-if ไม่บันทึก) */}
        <Route path="payouts" element={<AdminPayoutsPage />} /> {/* ⭐ NEW */}
        <Route path="deposit-orders" element={<AdminDepositOrdersPage />} /> {/* ⭐ P2P Deposit */}
        <Route path="p2p-payouts" element={<AdminP2pPayoutsPage />} /> {/* ⭐ P2P Payouts */}
        <Route path="p2p-overview" element={<AdminP2pOverviewPage />} /> {/* ⭐ P2P Overview */}
        <Route path="payout-topups" element={<PayoutTopupsPage />} /> {/* 💵 เติมเงิน Payout ของลูกค้า */}
        <Route path="bulk-transfer" element={<BulkTransferPage />} />
        <Route path="freeze" element={<FreezeManagementPage />} />
        <Route path="safe-wallet" element={<SafeWalletManagementPage />} />
        <Route path="usdt-wallet" element={<UsdtWalletPage />} />
        <Route path="company-transfer" element={<AdminCompanyTransferPage />} />
        <Route path="reduce-credit" element={<ReduceCreditPage />} /> {/* 🔻 ลดเครดิต Merchant */}
        <Route path="mismatched-accounts" element={<MismatchedAccountsPage />} />
        <Route path="employees" element={<AdminEmployeesPage />} /> {/* ⭐ Admin Employees */}
        <Route path="merchant-employees" element={<EmployeesReportPage />} /> {/* Merchant Employees Report */}
        <Route path="2fa" element={<Admin2FAPage />} /> {/* ⭐ 2FA Management */}
        <Route path="online-users" element={<OnlineUsersPage />} /> {/* 🟢 Online Users Monitor */}
        <Route path="webhook-logs" element={<WebhookLogsPage />} />
        <Route path="reports" element={<SystemReportsPage />} />
        <Route path="monthly-reports" element={<MonthlyReportsPage />} />
        <Route path="reconciliation" element={<ReconciliationPage />} />
        <Route path="fee-report" element={<FeeReportPage />} />
        <Route path="settings" element={<SystemSettingsPage />} />
        <Route path="ip-whitelist" element={<AdminIPWhitelistPage />} />
        <Route path="tickets" element={<AdminTicketsPage />} />
        <Route path="tickets/:id" element={<AdminTicketDetailPage />} />
        <Route path="agent-banks" element={<AgentBanksPage />} /> {/* ⭐ Agent Bank Management */}
        <Route path="agent-commissions" element={<AgentCommissionsPage />} /> {/* ⭐ Agent Commission Overview */}
        <Route path="agent-commissions/:id" element={<AgentCommissionDetailPage />} />
      </Route>

      {/* Agent Portal Routes */}
      <Route path="/agent/login" element={<AgentLoginPage />} />
      <Route path="/agent" element={
        <AgentProtectedRoute>
          <AgentLayout />
        </AgentProtectedRoute>
      }>
        <Route index element={<AgentDashboardPage />} />
        <Route path="transactions" element={<AgentTransactionsPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppRoutes />
          <ToastContainer position="bottom-right" style={{ bottom: '70px' }} autoClose={3000} limit={3} newestOnTop />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
